import { useCallback, useMemo } from 'react';

import { Theme, useTheme } from '@material-ui/core';

import { useSidebarPinState } from '@backstage/core-components';

import { Transformer } from '../transformer';
import { rules } from './rules';

/**
 * Sidebar pinned state to be used in computing style injections.
 */
const useSidebar = () => useSidebarPinState();

/**
 * Process all rules and concatenate their definitions into a single style.
 * @returns a string containing all processed style definitions.
 */
const useRuleStyles = () => {
  const sidebar = useSidebar();
  const theme = useTheme<Theme>();

  return useMemo(() => {
    const options = { theme, sidebar };
    return rules.reduce<string>((styles, rule) => styles + rule(options), '');
  }, [theme, sidebar]);
};

/**
 * Returns a transformer that inserts all style rules into the given element's head tag.
 */
export const useStylesTransformer = (): Transformer => {
  const styles = useRuleStyles();

  return useCallback(
    (dom: Element) => {
      const styleElement = dom.ownerDocument.createElement('style');
      styleElement.textContent = styles;
      dom.getElementsByTagName('head')[0].appendChild(styleElement);
      return dom;
    },
    [styles],
  );
};
